import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Mail, Facebook } from 'lucide-react';
import { SITE_COMPANY, SITE_CONTACT, SITE_SOCIAL, CONTACT_FORM_RECIPIENT_EMAIL } from '../../config/siteContact';
import { LineBrandIcon } from '../icons/LineBrandIcon';
import { AppDownloadButtons } from '../marketing/AppDownloadButtons';

const Footer: React.FC = () => {
  const facebookUrl =
    import.meta.env.VITE_SOCIAL_FACEBOOK_URL?.trim() || SITE_SOCIAL.facebookUrl;
  const lineUrl =
    import.meta.env.VITE_SOCIAL_LINE_URL?.trim() || SITE_SOCIAL.lineUrl;
  const facebookExternal = facebookUrl.startsWith('http');
  const lineExternal = lineUrl.startsWith('http');

  return (
    <footer className="fixnow-app-footer bg-gray-900 text-white pb-[calc(var(--fixnow-bottom-nav-h)+var(--fixnow-bottom-inset)+1.5rem)] md:pb-0">
      <div className="container mx-auto px-4 py-6 md:py-10">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-5 md:gap-8">
          <div>
            <Link to="/" className="flex items-center mb-2 md:mb-4 shrink-0">
              <img
                src="/brand-logo.png?v=10"
                alt="techfix"
                className="h-8 w-auto max-w-[180px] object-contain"
              />
            </Link>
            <p className="text-gray-400 mb-2 md:mb-4">
              為您連接專業工匠，解決所有居家維修需求。
            </p>
            <div className="flex space-x-4">
              <a
                href={facebookUrl}
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Facebook"
                target={facebookExternal ? '_blank' : undefined}
                rel={facebookExternal ? 'noopener noreferrer' : undefined}
              >
                <Facebook size={20} />
              </a>
              <a
                href={lineUrl}
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="LINE"
                target={lineExternal ? '_blank' : undefined}
                rel={lineExternal ? 'noopener noreferrer' : undefined}
              >
                <LineBrandIcon size={20} />
              </a>
            </div>
            <div className="mt-4 md:mt-6">
              <h3 className="text-sm font-medium text-gray-300 mb-2">下載 App</h3>
              <AppDownloadButtons variant="footer" />
            </div>
          </div>

          <div>
            <h3 className="text-lg font-medium mb-2 md:mb-4">服務項目</h3>
            <ul className="space-y-1.5 md:space-y-3">
              <li>
                <Link
                  to={`/search?q=${encodeURIComponent('水電')}`}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  水電維修
                </Link>
              </li>
              <li>
                <Link
                  to={`/search?q=${encodeURIComponent('電器')}`}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  電器維修
                </Link>
              </li>
              <li>
                <Link
                  to={`/search?q=${encodeURIComponent('空調')}`}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  空調維修
                </Link>
              </li>
              <li>
                <Link
                  to={`/search?q=${encodeURIComponent('維修')}`}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  一般維修
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-medium mb-2 md:mb-4">關於我們</h3>
            <ul className="space-y-1.5 md:space-y-3">
              <li><Link to="/about" className="text-gray-400 hover:text-white transition-colors">公司簡介</Link></li>
              <li><Link to="/about" className="text-gray-400 hover:text-white transition-colors">服務介紹</Link></li>
              <li><Link to="/blog" className="text-gray-400 hover:text-white transition-colors">部落格</Link></li>
              <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors">聯絡我們</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-medium mb-2 md:mb-4">法律資訊</h3>
            <ul className="space-y-1.5 md:space-y-3">
              <li><Link to="/legal" className="text-gray-400 hover:text-white transition-colors">法律文檔</Link></li>
              <li><Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">隱私政策</Link></li>
              <li><Link to="/terms" className="text-gray-400 hover:text-white transition-colors">服務條款</Link></li>
              <li><Link to="/user-agreement" className="text-gray-400 hover:text-white transition-colors">用戶協議</Link></li>
              <li><Link to="/refund-policy" className="text-gray-400 hover:text-white transition-colors">退款政策</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-medium mb-2 md:mb-4">聯絡資訊</h3>
            <ul className="space-y-1.5 md:space-y-3">
              <li className="flex items-center">
                <MapPin size={18} className="mr-2 text-gray-400 shrink-0" />
                <span className="text-gray-400">{SITE_CONTACT.address}</span>
              </li>
              <li className="flex items-center">
                <Mail size={18} className="mr-2 text-gray-400 shrink-0" />
                <a
                  href={`mailto:${CONTACT_FORM_RECIPIENT_EMAIL}`}
                  className="text-gray-400 hover:text-white transition-colors break-all"
                  title={`寄信至 ${CONTACT_FORM_RECIPIENT_EMAIL}`}
                >
                  {SITE_CONTACT.supportEmail}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-6 pt-4 md:mt-10 md:pt-6 border-t border-gray-800">
          <p className="text-center text-gray-500 text-sm mb-2">
            {SITE_COMPANY.legalName} · 統一編號 {SITE_COMPANY.taxId}
          </p>
          <p className="text-center text-gray-400">
            © {new Date().getFullYear()} {SITE_COMPANY.brandName}. 版權所有.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;